import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SparklesIcon,
  XMarkIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { aiApi, AiMessage } from '../../api/carbon';
import { assistantApi } from '../../api/assistant';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SUGGESTED_PROMPTS = [
  'Analyse mes encours et propose des relances',
  'Quels clients sont les plus en retard ce mois-ci ?',
  'Récapitulatif TVA du mois en cours',
  'Quel est mon CO₂e moyen par facture ?',
];

function extractText(response: any): string {
  if (!response) return '';
  if (typeof response === 'string') return response;
  // Common shapes from GTS / Anthropic-compatible APIs
  if (typeof response.text === 'string') return response.text;
  if (typeof response.content === 'string') return response.content;
  if (Array.isArray(response.content)) {
    return response.content
      .map((c: any) => (typeof c === 'string' ? c : c.text || ''))
      .join('');
  }
  if (Array.isArray(response.messages) && response.messages.length) {
    const last = response.messages[response.messages.length - 1];
    return typeof last?.content === 'string' ? last.content : extractText(last?.content);
  }
  if (response.message && typeof response.message.content === 'string') {
    return response.message.content;
  }
  if (response.choices && response.choices[0]?.message?.content) {
    return response.choices[0].message.content;
  }
  return JSON.stringify(response);
}

export function AISidebar({ open, onClose }: Props) {
  const location = useLocation();
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setError(null);
    const next: AiMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(next);
    setDraft('');
    setBusy(true);
    try {
      // Prefer the TuniFact-native dashboard assistant (sees the dashboard
      // snapshot server-side). Fall back to the GTS Carbon bridge when the
      // local endpoint is unavailable (404 = backend without spec 09) or
      // returns 503 (LLM disabled / provider down).
      let replyText = '';
      try {
        const local = await assistantApi.ask({ question: trimmed, history: messages });
        replyText = local.answer || '';
      } catch (localErr: any) {
        const localStatus = localErr?.response?.status;
        // Only fall back to GTS Carbon when the local endpoint is genuinely
        // unavailable. 402 (plan) and 403 (role) are user-facing decisions —
        // surfacing them directly is more helpful than silently retrying.
        if (localStatus !== 404 && localStatus !== 503) throw localErr;
        const resp = await aiApi.chat(next, { page: location.pathname });
        replyText = extractText(resp);
      }
      setMessages((m) => [...m, { role: 'assistant', content: replyText || '(réponse vide)' }]);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 402) {
        setError("L'assistant IA est disponible avec le plan BUSINESS uniquement.");
      } else if (status === 403) {
        setError("L'assistant IA est réservé aux rôles administrateur et comptable.");
      } else if (status === 503) {
        setError("L'assistant IA est temporairement indisponible. Réessayez dans quelques instants.");
      } else if (status === 502) {
        setError("Le fournisseur IA est injoignable. Réessayez dans quelques instants.");
      } else if (!status) {
        setError('Erreur réseau — vérifiez votre connexion.');
      } else {
        setError(err?.response?.data?.error || `Erreur (${status})`);
      }
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(draft);
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-30 lg:hidden"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-2xl border-l border-gray-200 z-40 flex flex-col"
        role="complementary"
        aria-label="Assistant IA"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-white">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 text-white">
              <SparklesIcon className="w-4 h-4" />
            </span>
            <div>
              <div className="text-sm font-semibold text-gray-900">Assistant IA</div>
              <div className="text-[11px] text-emerald-700">TuniFact + GTS Carbon</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            title="Fermer"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.length === 0 ? (
            <div className="space-y-3">
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-sm text-emerald-900">
                Bonjour, je peux analyser vos données fiscales et carbone.
                Quelques pistes pour démarrer :
              </div>
              <div className="space-y-2">
                {SUGGESTED_PROMPTS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => send(p)}
                    className="w-full text-left rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 transition"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))
          )}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-500 text-sm rounded-2xl px-3 py-2 inline-flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                Réflexion…
              </div>
            </div>
          )}
          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <form onSubmit={onSubmit} className="border-t border-gray-100 p-3 bg-white">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(draft);
                }
              }}
              rows={2}
              placeholder="Posez une question métier…"
              disabled={busy}
              className="flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
            <button
              type="submit"
              disabled={busy || !draft.trim()}
              className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 text-white hover:from-emerald-600 hover:to-green-700 disabled:opacity-40 transition"
              title="Envoyer"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
            </button>
          </div>
          <p className="mt-1.5 text-[10px] text-gray-400">
            Entrée pour envoyer, Maj+Entrée pour un saut de ligne.
          </p>
        </form>
      </aside>
    </>
  );
}

export default AISidebar;
